import { generateSlug } from "./generateSlug";

/**
 * Parses HTML content and adds ID attributes to heading tags (h1-h6)
 * based on their text content.
 *
 * @param htmlString - The HTML string to process
 * @returns The processed HTML string with IDs added to heading tags
 *
 * @example
 * const html = '<h2>Aufhebungsvertrag Betriebsrat</h2>';
 * const result = addIdsToHeadings(html);
 * // Returns: '<h2 id="aufhebungsvertrag-betriebsrat">Aufhebungsvertrag Betriebsrat</h2>'
 */
export function addIdsToHeadings(htmlString: string): string {
  // Guard against undefined/null input
  if (!htmlString) {
    return htmlString ?? "";
  }

  // Match heading tags (h1-h6) with their content
  const headingRegex = /<(h[1-6])([^>]*)>(.*?)<\/\1>/gi;

  // Track used IDs to handle duplicates
  const usedIds = new Set<string>();

  return htmlString.replace(headingRegex, (match, tag, attributes, content) => {
    // Check if an ID already exists
    if (attributes.includes("id=")) {
      return match; // Don't modify if ID already exists
    }

    // Extract plain text from content (strip HTML tags)
    const plainText = content.replace(/<[^>]*>/g, "");

    // Generate slug from the text content
    const slug = generateSlug(plainText);

    // Handle duplicate IDs by appending a number
    let finalSlug = slug;
    let counter = 2;
    while (usedIds.has(finalSlug)) {
      finalSlug = `${slug}-${counter}`;
      counter++;
    }
    usedIds.add(finalSlug);

    // Add ID attribute to the opening tag
    const newAttributes = attributes.trim()
      ? `${attributes} id="${finalSlug}"`
      : ` id="${finalSlug}"`;

    return `<${tag}${newAttributes}>${content}</${tag}>`;
  });
}
